import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Users, ShoppingBag, Mail, DollarSign, 
  RefreshCw, UserPlus, UserMinus, ChevronDown, ChevronUp,
  TrendingUp, Eye, BarChart3, Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

export default function Admin() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [grantEmail, setGrantEmail] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);
  
  // New user management states
  const [resetEmail, setResetEmail] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserGrantGuide, setNewUserGrantGuide] = useState(true);
  
  // PDF upload state
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    // Wait for auth to load before checking
    if (authLoading) return;
    
    if (!user || !token) {
      navigate('/login?redirect=/admin');
      return;
    }
    fetchData();
  }, [user, token, authLoading, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, ordersRes, usersRes, subscribersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { headers }),
        axios.get(`${API_URL}/api/admin/orders`, { headers }),
        axios.get(`${API_URL}/api/admin/users`, { headers }),
        axios.get(`${API_URL}/api/admin/subscribers`, { headers }),
      ]);
      
      setStats(statsRes.data);
      setOrders(ordersRes.data.orders);
      setUsers(usersRes.data.users);
      setSubscribers(subscribersRes.data.subscribers);
    } catch (error) {
      
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGrantAccess = async (e) => {
    e.preventDefault();
    if (!grantEmail) return;
    
    try {
      await axios.post(
        `${API_URL}/api/admin/grant-access?email=${encodeURIComponent(grantEmail)}&product_id=signal-full-guide`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Access granted to ${grantEmail}`);
      setGrantEmail('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant access');
    }
  };

  const handleRevokeAccess = async (email) => {
    if (!confirm(`Revoke access for ${email}?`)) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/admin/revoke-access?email=${encodeURIComponent(email)}&product_id=signal-full-guide`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Access revoked from ${email}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail || !resetPassword) {
      toast.error('Email and password required');
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/admin/reset-user-password?email=${encodeURIComponent(resetEmail)}&new_password=${encodeURIComponent(resetPassword)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Password reset for ${resetEmail}`);
      setResetEmail('');
      setResetPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUserEmail || !newUserPassword) {
      toast.error('Email and password required');
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/admin/create-user?email=${encodeURIComponent(newUserEmail)}&password=${encodeURIComponent(newUserPassword)}&name=${encodeURIComponent(newUserName || 'Customer')}&grant_guide=${newUserGrantGuide}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User ${newUserEmail} created`);
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserName('');
      setNewUserGrantGuide(true);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleQuickResetPassword = async (email) => {
    const newPass = 'Signal2024!';
    if (!confirm(`Reset password for ${email} to "${newPass}"?`)) return;
    
    try {
      await axios.post(
        `${API_URL}/api/admin/reset-user-password?email=${encodeURIComponent(email)}&new_password=${encodeURIComponent(newPass)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Password reset to ${newPass}`);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-black pt-28 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="min-h-screen bg-black pt-28" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <motion.div 
          initial="hidden" 
          animate="visible" 
          variants={staggerContainer}
          className="mb-12"
        >
          <motion.div variants={fadeInUp} className="flex items-center justify-between">
            <div>
              <h1 className="font-serif text-3xl md:text-4xl text-white mb-2">Admin Dashboard</h1>
              <p className="text-[#999999]">Manage orders, users, and content</p>
            </div>
            <Button
              onClick={fetchData}
              variant="outline"
              className="border-[#333] text-white hover:bg-[#1a1a1a]"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </motion.div>
        </motion.div>

        {/* Stats Cards */}
        {stats && (
          <motion.div 
            initial="hidden" 
            animate="visible" 
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
          >
            <motion.div variants={fadeInUp} className="bg-[#0a0a0a] border border-[#1a1a1a] p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#1a1a1a] flex items-center justify-center">
                  <ShoppingBag className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <span className="text-[#999999] text-sm">Orders</span>
              </div>
              <p className="text-3xl font-serif text-white">{stats.total_orders}</p>
            </motion.div>
            
            <motion.div variants={fadeInUp} className="bg-[#0a0a0a] border border-[#1a1a1a] p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#1a1a1a] flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <span className="text-[#999999] text-sm">Revenue</span>
              </div>
              <p className="text-3xl font-serif text-white">€{stats.total_revenue?.toFixed(2) || '0.00'}</p>
            </motion.div>
            
            <motion.div variants={fadeInUp} className="bg-[#0a0a0a] border border-[#1a1a1a] p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#1a1a1a] flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <span className="text-[#999999] text-sm">Users</span>
              </div>
              <p className="text-3xl font-serif text-white">{stats.total_users}</p>
            </motion.div>
            
            <motion.div variants={fadeInUp} className="bg-[#0a0a0a] border border-[#1a1a1a] p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#1a1a1a] flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <span className="text-[#999999] text-sm">Subscribers</span>
              </div>
              <p className="text-3xl font-serif text-white">{stats.total_subscribers}</p>
            </motion.div>
          </motion.div>
        )}

        {/* Conversion Stats */}
        {stats && (
          <motion.div 
            initial="hidden" 
            animate="visible" 
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12"
          >
            <motion.div variants={fadeInUp} className="bg-[#0a0a0a] border border-[#1a1a1a] p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#1a1a1a] flex items-center justify-center">
                  <Eye className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <span className="text-[#999999] text-sm">Homepage Views</span>
              </div>
              <p className="text-3xl font-serif text-white">{stats.total_homepage_views || 0}</p>
            </motion.div>
            
            <motion.div variants={fadeInUp} className="bg-[#0a0a0a] border border-[#1a1a1a] p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#1a1a1a] flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <span className="text-[#999999] text-sm">Product Page Views</span>
              </div>
              <p className="text-3xl font-serif text-white">{stats.total_product_views || 0}</p>
            </motion.div>
            
            <motion.div variants={fadeInUp} className="bg-[#0a0a0a] border border-[#D4AF37] p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#D4AF37]/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <span className="text-[#D4AF37] text-sm font-medium">Conversion Rate</span>
              </div>
              <p className="text-3xl font-serif text-[#D4AF37]">{stats.conversion_rate || 0}%</p>
              <p className="text-xs text-[#999999] mt-1">Visitors → Orders</p>
            </motion.div>
          </motion.div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-[#1a1a1a] overflow-x-auto">
          {['overview', 'orders', 'users', 'subscribers', 'settings'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm capitalize transition-colors whitespace-nowrap ${
                activeTab === tab 
                  ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' 
                  : 'text-[#999999] hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Grant Access Form */}
              <div className="bg-[#0a0a0a] border border-[#1a1a1a] p-6">
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-[#D4AF37]" />
                  Grant Manual Access
                </h3>
                <form onSubmit={handleGrantAccess} className="flex gap-4">
                  <input
                    type="email"
                    value={grantEmail}
                    onChange={(e) => setGrantEmail(e.target.value)}
                    placeholder="Enter user email"
                    className="flex-1 bg-[#0f0f0f] border border-[#333] px-4 py-2 text-white placeholder-[#666] focus:outline-none focus:border-[#D4AF37]"
                  />
                  <Button type="submit" className="bg-[#D4AF37] text-black hover:bg-[#C9A030]">
                    Grant Access
                  </Button>
                </form>
              </div>

              {/* Recent Orders */}
              <div className="bg-[#0a0a0a] border border-[#1a1a1a] p-6">
                <h3 className="text-white font-medium mb-4">Recent Orders</h3>
                <div className="space-y-3">
                  {stats?.recent_orders?.slice(0, 5).map((order, idx) => (
                    <div key={idx} className="flex items-center justify-between py-3 border-b border-[#1a1a1a] last:border-0">
                      <div>
                        <p className="text-white">{order.customer_email}</p>
                        <p className="text-[#666] text-sm">
                          {new Date(order.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <p className="text-[#D4AF37]">€{order.total_price}</p>
                    </div>
                  )) || <p className="text-[#666]">No orders yet</p>}
                </div>
              </div>
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="bg-[#0a0a0a] border border-[#1a1a1a]">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#1a1a1a]">
                      <th className="text-left p-4 text-[#999] text-sm font-normal">Order #</th>
                      <th className="text-left p-4 text-[#999] text-sm font-normal">Customer</th>
                      <th className="text-left p-4 text-[#999] text-sm font-normal">Products</th>
                      <th className="text-left p-4 text-[#999] text-sm font-normal">Amount</th>
                      <th className="text-left p-4 text-[#999] text-sm font-normal">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order, idx) => (
                      <tr key={idx} className="border-b border-[#1a1a1a] hover:bg-[#0f0f0f]">
                        <td className="p-4 text-white">#{order.shopify_order_number || 'N/A'}</td>
                        <td className="p-4 text-white">{order.customer_email}</td>
                        <td className="p-4 text-[#999]">{order.products?.join(', ')}</td>
                        <td className="p-4 text-[#D4AF37]">€{order.total_price}</td>
                        <td className="p-4 text-[#999]">{new Date(order.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {orders.length === 0 && (
                  <p className="p-8 text-center text-[#666]">No orders yet</p>
                )}
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              {/* User Management Tools */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Create New User */}
                <div className="bg-[#0a0a0a] border border-[#1a1a1a] p-6">
                  <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-[#D4AF37]" />
                    Create New User
                  </h3>
                  <form onSubmit={handleCreateUser} className="space-y-3">
                    <input
                      type="email"
                      value={newUserEmail}
                      onChange={(e) => setNewUserEmail(e.target.value)}
                      placeholder="Email"
                      className="w-full px-4 py-2 bg-[#1a1a1a] border border-[#333] text-white placeholder-[#666] focus:border-[#D4AF37] outline-none"
                    />
                    <input
                      type="text"
                      value={newUserPassword}
                      onChange={(e) => setNewUserPassword(e.target.value)}
                      placeholder="Password"
                      className="w-full px-4 py-2 bg-[#1a1a1a] border border-[#333] text-white placeholder-[#666] focus:border-[#D4AF37] outline-none"
                    />
                    <input
                      type="text"
                      value={newUserName}
                      onChange={(e) => setNewUserName(e.target.value)}
                      placeholder="Name (optional)"
                      className="w-full px-4 py-2 bg-[#1a1a1a] border border-[#333] text-white placeholder-[#666] focus:border-[#D4AF37] outline-none"
                    />
                    <label className="flex items-center gap-2 text-[#999] text-sm">
                      <input
                        type="checkbox"
                        checked={newUserGrantGuide}
                        onChange={(e) => setNewUserGrantGuide(e.target.checked)}
                        className="accent-[#D4AF37]"
                      />
                      Grant guide access
                    </label>
                    <Button type="submit" className="w-full bg-[#D4AF37] text-black hover:bg-[#C9A030]">
                      Create User
                    </Button>
                  </form>
                </div>

                {/* Reset Password */}
                <div className="bg-[#0a0a0a] border border-[#1a1a1a] p-6">
                  <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 text-[#D4AF37]" />
                    Reset User Password
                  </h3>
                  <form onSubmit={handleResetPassword} className="space-y-3">
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="User email"
                      className="w-full px-4 py-2 bg-[#1a1a1a] border border-[#333] text-white placeholder-[#666] focus:border-[#D4AF37] outline-none"
                    />
                    <input
                      type="text"
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      placeholder="New password"
                      className="w-full px-4 py-2 bg-[#1a1a1a] border border-[#333] text-white placeholder-[#666] focus:border-[#D4AF37] outline-none"
                    />
                    <Button type="submit" className="w-full bg-[#D4AF37] text-black hover:bg-[#C9A030]">
                      Reset Password
                    </Button>
                  </form>
                </div>
              </div>

              {/* Users Table */}
              <div className="bg-[#0a0a0a] border border-[#1a1a1a]">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#1a1a1a]">
                        <th className="text-left p-4 text-[#999] text-sm font-normal">Email</th>
                        <th className="text-left p-4 text-[#999] text-sm font-normal">Name</th>
                        <th className="text-left p-4 text-[#999] text-sm font-normal">Products</th>
                        <th className="text-left p-4 text-[#999] text-sm font-normal">Joined</th>
                        <th className="text-left p-4 text-[#999] text-sm font-normal">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u, idx) => (
                        <tr key={idx} className="border-b border-[#1a1a1a] hover:bg-[#0f0f0f]">
                          <td className="p-4 text-white">{u.email}</td>
                          <td className="p-4 text-[#999]">{u.name || '-'}</td>
                          <td className="p-4">
                            {u.purchased_products?.length > 0 ? (
                              <span className="text-[#D4AF37] text-sm">{u.purchased_products.join(', ')}</span>
                            ) : (
                              <span className="text-[#666] text-sm">None</span>
                            )}
                          </td>
                          <td className="p-4 text-[#999]">{new Date(u.created_at).toLocaleDateString()}</td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleQuickResetPassword(u.email)}
                                className="text-[#D4AF37] hover:text-[#C9A030] text-sm"
                                title="Reset to Signal2024!"
                              >
                                Reset PW
                              </button>
                              {u.purchased_products?.includes('signal-full-guide') ? (
                                <button
                                  onClick={() => handleRevokeAccess(u.email)}
                                  className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1"
                                >
                                  <UserMinus className="w-4 h-4" />
                                  Revoke
                                </button>
                              ) : (
                                <button
                                  onClick={async () => {
                                    try {
                                      await axios.post(
                                        `${API_URL}/api/admin/grant-access?email=${encodeURIComponent(u.email)}&product_id=signal-full-guide`,
                                        {},
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      toast.success(`Access granted to ${u.email}`);
                                      fetchData();
                                    } catch (error) {
                                      toast.error('Failed to grant access');
                                    }
                                  }}
                                  className="text-green-400 hover:text-green-300 text-sm flex items-center gap-1"
                                >
                                  <UserPlus className="w-4 h-4" />
                                  Grant
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {users.length === 0 && (
                    <p className="p-8 text-center text-[#666]">No users yet</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Subscribers Tab */}
          {activeTab === 'subscribers' && (
            <div className="bg-[#0a0a0a] border border-[#1a1a1a]">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#1a1a1a]">
                      <th className="text-left p-4 text-[#999] text-sm font-normal">Email</th>
                      <th className="text-left p-4 text-[#999] text-sm font-normal">Status</th>
                      <th className="text-left p-4 text-[#999] text-sm font-normal">Subscribed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((sub, idx) => (
                      <tr key={idx} className="border-b border-[#1a1a1a] hover:bg-[#0f0f0f]">
                        <td className="p-4 text-white">{sub.email}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-xs ${
                            sub.status === 'active' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-[#333] text-[#999]'
                          }`}>
                            {sub.status || 'active'}
                          </span>
                        </td>
                        <td className="p-4 text-[#999]">{new Date(sub.subscribed_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {subscribers.length === 0 && (
                  <p className="p-8 text-center text-[#666]">No subscribers yet</p>
                )}
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              {/* Upload Guide PDF */}
              <div className="bg-[#0a0a0a] border border-[#1a1a1a] p-6">
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-[#D4AF37]" />
                  Upload Guide PDF
                </h3>
                <p className="text-[#666] text-sm mb-4">
                  Upload the Signal guide PDF. This enables the guide preview on the homepage and the secure reader for customers.
                </p>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    
                    if (!file.name.endsWith('.pdf')) {
                      toast.error('Only PDF files allowed');
                      return;
                    }
                    
                    setUploading(true);
                    const formData = new FormData();
                    formData.append('file', file);
                    
                    try {
                      await axios.post(`${API_URL}/api/admin/upload-guide`, formData, {
                        headers: { 
                          Authorization: `Bearer ${token}`,
                          'Content-Type': 'multipart/form-data'
                        }
                      });
                      toast.success('Guide PDF uploaded successfully!');
                    } catch (error) {
                      toast.error('Failed to upload PDF');
                    } finally {
                      setUploading(false);
                    }
                  }}
                  className="block w-full text-sm text-[#999] file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:font-semibold file:bg-[#D4AF37] file:text-black hover:file:bg-[#C9A030] file:cursor-pointer"
                  disabled={uploading}
                />
                {uploading && <p className="text-[#D4AF37] text-sm mt-2">Uploading...</p>}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
